import React, { useState, useEffect } from "react";
import { Table, Button, Typography, Spin, Input } from "antd";
import { PlusOutlined, MinusOutlined, DownloadOutlined } from "@ant-design/icons";
import { json } from "react-router-dom";

const { Text } = Typography;

function ChildFieldRendererSource({ data, innerValue, expandedChildKeys, onChildToggle, path, childTitlefield }) {
  const uniqueKey = `${path}.${data}`;
  const open = expandedChildKeys[uniqueKey] || false;

  const allSourceNone =
  typeof innerValue === "object" &&
  Object.values(innerValue)
    .filter(v => v && typeof v === "object" && "source" in v)
    .every(v => v.source === "None");


  if (data !== "isDifferent" && data !== "isChild" && data !== "source") {
    // determine relationship name from path (path === "<record>.<relationship>")
    const rel = path ? path.split('.').slice(-1)[0] : null;
    // childTitlefield may be a map (object) or a string - support both
    let titleFieldName = childTitlefield;
    if (childTitlefield && typeof childTitlefield === 'object') {
      titleFieldName = (rel && childTitlefield[rel]) ? childTitlefield[rel] : Object.values(childTitlefield)[0];
    }

    return (
      <li style={{ color: innerValue.isDifferent ? "red" : "green" }}>
        <Button
          type="primary"
          shape="circle"
          size="small"
          icon={open ? <MinusOutlined /> : <PlusOutlined />}
          onClick={() => onChildToggle(uniqueKey)}
          style={{
            marginRight: 6,
            background: innerValue.isDifferent ? "#FF6868" : "#508D69",
          }}
        />
        <Text strong>{allSourceNone ? "Record Not Found" : innerValue?.[titleFieldName]?.source}:</Text>{" "}
        {open && (
          <ul style={{ marginTop: 8 }}>
            {Object.entries(innerValue).map(([k, v]) => (
              <FieldRendererSource
                key={k}
                data={k}
                innervalue={v}
                expandedChildKeys={expandedChildKeys}
                onChildToggle={onChildToggle}
                childTitlefield={childTitlefield}
                path={uniqueKey}
              />
            ))}
          </ul>
        )}
      </li>
    );
  }
  return null;
}

// ✅ Nested object (Source)
function FieldRendererSource({ data, innervalue, globalExpand, expandedChildKeys, onChildToggle, path, childTitlefield }) {
  const uniqueKey = `${path}.${data}`;
  const open = expandedChildKeys[uniqueKey] || globalExpand;
  // console.log('FieldRendererSource called with data00:', data, 'innervalue:', innervalue, 'path:', path, 'childTitlefield:', childTitlefield);

  if (innervalue?.isChild) {
    return (
      <li>
        <Button
          type="primary"
          shape="circle"
          size="small"
          icon={open ? <MinusOutlined /> : <PlusOutlined />}
          onClick={() => onChildToggle(uniqueKey)}
          style={{
            marginRight: 6,
            background: innervalue.isDifferent ? "#FF6868" : "#508D69"
          }}
        />
        <Text strong style={{ color: innervalue.isDifferent ? "red" : "green" }}>
          {data}:
        </Text>
        {open && (
          <ul style={{ marginTop: 8 }}>
            {Object.entries(innervalue).map(([k, v]) => (
              <ChildFieldRendererSource
                key={k}
                data={k}
                innerValue={v}
                expandedChildKeys={expandedChildKeys}
                onChildToggle={onChildToggle}
                childTitlefield={childTitlefield}
                path={uniqueKey}
              />
            ))}
          </ul>
        )}
      </li>
    );
  }

  if (innervalue) {
    return (
      <li style={{ color: innervalue.isDifferent ? "red" : "green" }}>
        <Text strong>{data}:</Text>{" "}
        {innervalue.source != null ? innervalue.source.toString() : ""}
      </li>
    );
  }

  return null;
}

// ✅ Leaf (Destination)
function ChildFieldRendererDestination({ data, innerValue, expandedChildKeys, onChildToggle, path, childTitlefield }) {
  const uniqueKey = `${path}.${data}`;
  const open = expandedChildKeys[uniqueKey] || false;

  const allTargetsNone =
    typeof innerValue === "object" &&
    Object.values(innerValue)
      .filter(v => v && typeof v === "object" && "target" in v)
      .every(v => v.target === "None");

  if (data !== "isDifferent" && data !== "isChild" && data !== "target") {
    const rel = path ? path.split('.').slice(-1)[0] : null;
    let titleFieldName = childTitlefield;
    if (childTitlefield && typeof childTitlefield === 'object') {
      titleFieldName = (rel && childTitlefield[rel]) ? childTitlefield[rel] : Object.values(childTitlefield)[0];
    }

    return (
      <li style={{ color: innerValue.isDifferent ? "red" : "green" }}>
        <Button
          type="primary"
          shape="circle"
          size="small"
          icon={open ? <MinusOutlined /> : <PlusOutlined />}
          onClick={() => onChildToggle(uniqueKey)}
          style={{
            marginRight: 6,
            background: innerValue.isDifferent ? "#FF6868" : "#508D69",
          }}
        />
        <Text strong>{allTargetsNone ? "Record Not Found" : innerValue?.[titleFieldName]?.target}:</Text>{" "}
        {open && (
          <ul style={{ marginTop: 8 }}>
            {Object.entries(innerValue).map(([k, v]) => (
              <FieldRendererDestination
                key={k}
                data={k}
                innervalue={v}
                expandedChildKeys={expandedChildKeys}
                onChildToggle={onChildToggle}
                childTitlefield={childTitlefield}
                path={uniqueKey}
              />
            ))}
          </ul>
        )}
      </li>
    );
  }
  return null;
}

// ✅ Nested object (Destination)
function FieldRendererDestination({ data, innervalue, globalExpand, expandedChildKeys, onChildToggle, path, childTitlefield }) {
  const uniqueKey = `${path}.${data}`;
  const open = expandedChildKeys[uniqueKey] || globalExpand;
  if (innervalue?.isChild) {
    return (
      <li>
        <Button
          type="primary"
          shape="circle"
          size="small"
          icon={open ? <MinusOutlined /> : <PlusOutlined />}
          onClick={() => onChildToggle(uniqueKey)}
          style={{
            marginRight: 6,
            background: innervalue.isDifferent ? "#FF6868" : "#508D69",
          }}
        />
        <Text strong style={{ color: innervalue.isDifferent ? "red" : "green" }}>
          {data}:
        </Text>
        {open && (
          <ul style={{ marginTop: 8 }}>
            {Object.entries(innervalue).map(([k, v]) => (
              <ChildFieldRendererDestination
                key={k}
                data={k}
                innerValue={v}
                expandedChildKeys={expandedChildKeys}
                onChildToggle={onChildToggle}
                childTitlefield={childTitlefield}
                path={uniqueKey}
              />
            ))}
          </ul>
        )}
      </li>
    );
  }

  if (innervalue) {
    return (
      <li style={{ color: innervalue.isDifferent ? "red" : "green" }}>
        <Text strong>{data}:</Text>{" "}
        {innervalue.target != null ? innervalue.target.toString() : ""}
      </li>
    );
  }

  return null;
}

// ✅ Row Renderer (Parent level)
function ChildRowRenderer({ data, innerValue, globalExpand, expandedChildKeys, onChildToggle, titlefield, childTitlefield }) {
  const path = data;
  const open = expandedChildKeys[path] || false;
  // console.log('ChildRowRenderer called with data:', data, 'innerValue:', innerValue, 'titlefield:', titlefield, 'childTitlefield:', childTitlefield);
//Here also getting only childdata without the title field value
  return {
    key: data,
    source: (
      <div>
        <Button
          type="primary"
          shape="circle"
          size="small"
          icon={open ? <MinusOutlined /> : <PlusOutlined />}
          onClick={() => onChildToggle(path)}
          style={{
            background: innerValue.isDifferent ? "#FF6868" : "#508D69",
            marginRight: 8,
          }}
        />
        <Text strong style={{ fontSize: 14 }}>
          {innerValue?.source === "None" || innerValue?.[titlefield]?.source === "Record Not Found" ? (
            "Record Not found"
          ) : (
            <a href={innerValue?.source_link} target="_blank" rel="noopener noreferrer">
              {innerValue?.[titlefield]?.source}
            </a>
          )}
        </Text>
        {open && (
          <ul style={{ marginTop: 8 }}>
            {Object.entries(innerValue).map(([k, v]) =>
              (k !== "isDifferent" && k !== "source" && k !== "target_link" && k !== "source_link") ? (
                <FieldRendererSource
                  key={k}
                  data={k}
                  innervalue={v}
                  globalExpand={globalExpand}
                  expandedChildKeys={expandedChildKeys}
                  onChildToggle={onChildToggle}
                  childTitlefield={childTitlefield}
                  path={path}
                />
              ) : null
            )}
          </ul>
        )}
      </div>
    ),
    destination: (
      <div>
        <Button
          type="primary"
          shape="circle"
          size="small"
          icon={open ? <MinusOutlined /> : <PlusOutlined />}
          onClick={() => onChildToggle(path)}
          style={{
            background: innerValue.isDifferent ? "#FF6868" : "#508D69",
            marginRight: 8,
          }}
        />
        <Text strong style={{ fontSize: 14 }}>
          {innerValue?.target === "None" || innerValue?.[titlefield]?.target === "Record Not Found" ? (
            "Record Not found"
          ) : (
            <a href={innerValue?.target_link} target="_blank" rel="noopener noreferrer">
              {innerValue?.[titlefield]?.target}
            </a>
          )}
        </Text>
        {open && (
          <ul style={{ marginTop: 8 }}>
            {Object.entries(innerValue).map(([k, v]) =>
              (k !== "isDifferent" && k !== "target" && k !== "target_link" && k !== "source_link") ? (
                <FieldRendererDestination
                  key={k}
                  data={k}
                  innervalue={v}
                  globalExpand={globalExpand}
                  expandedChildKeys={expandedChildKeys}
                  onChildToggle={onChildToggle}
                  childTitlefield = {childTitlefield}
                  path={path}
                />
              ) : null
            )}
          </ul>
        )}
      </div>
    ),
    data: data.toLowerCase()
  };
}


const flattenDiffData = (data) => {
  const rows = [];

  Object.entries(data).forEach(([recordName, recordValue]) => {
    Object.entries(recordValue).forEach(([field, fieldValue]) => {
      if (field !== "isDifferent" && field !== 'source') {
        if (typeof fieldValue === "object" && fieldValue !== null) {
          rows.push({
            recordName,
            field,
            source: fieldValue.source ?? "",
            target: fieldValue.target ?? "",
            isDifferent: fieldValue.isDifferent ?? false,
          });
        }
      }
    });
  });

  return rows;
};

// 🔥 Helper: convert to CSV string
const toCSV = (rows) => {
  const header = ["Record Name", "Field", "Source", "Target", "isDifferent"];
  const csvRows = [header.join(",")];

  rows.forEach((row) => {
    csvRows.push(
      [
        `"${row.recordName}"`,
        `"${row.field}"`,
        `"${row.source}"`,
        `"${row.target}"`,
        row.isDifferent ? "DIFFERENT" : "SAME",
      ].join(",")
    );
  });

  return csvRows.join("\n");
};

// 🔥 Helper: trigger file download
const downloadCSV = (data) => {
  const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "diff-data.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};



function MyTableComponent({ data, sourcename, destinationname, setSelectedDiv, sourceRecords, destinationRecords, titlefield, childTitlefield }) {
  const [globalExpand, setGlobalExpand] = useState(false);
  const [loading, setLoading] = useState(false);
  const [onlydiffRecords, setOnlyDiffRecords] = useState(false);
  const [searchText, setSearchText] = useState(""); // 🔹 Search state
  const [expandedChildKeys, setExpandedChildKeys] = useState({});
  const onChildToggle = (key) => {
    setExpandedChildKeys((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };
  // console.log("Data in Table Component:", data);
  // console.log("ChildTitleField in Table Component:", childTitlefield);

  const toggleDiffRecords = () => {
    setOnlyDiffRecords(!onlydiffRecords);
  };

  // 🔹 Helper to collect all unique keys in data
  const getAllKeys = (obj, path = "") => {
    let keys = {};
    Object.entries(obj).forEach(([key, value]) => {
      const uniqueKey = path ? `${path}.${key}` : key;
      keys[uniqueKey] = true;
      if (value && typeof value === "object") {
        keys = { ...keys, ...getAllKeys(value, uniqueKey) };
      }
    });
    return keys;
  };

  // 🔹 Expand/Collapse All
  const toggleExpand = () => {
    setLoading(true);
    setTimeout(() => {
      if (!globalExpand) {
        // Expand all
        const allKeys = getAllKeys(data);
        setExpandedChildKeys(allKeys);
      } else {
        // Collapse all
        setExpandedChildKeys({});
      }
      setGlobalExpand(!globalExpand);
      setLoading(false);
    }, 300);
  };

  // 🔹 Build rows
  const rows = Object.entries(data).map(([row, index]) =>
    ChildRowRenderer({ data: row, innerValue: index, globalExpand, expandedChildKeys, onChildToggle, titlefield, childTitlefield })
  );

  const diffrows = Object.entries(data)
    .filter(([row]) => row !== "isDifferent")
    .filter(([_, value]) => value?.isDifferent === true)
    .map(([row, index]) =>
      ChildRowRenderer({ data: row, innerValue: index, globalExpand, expandedChildKeys, onChildToggle, titlefield, childTitlefield })
    );

  // 🔹 Apply search filter
  const filteredRows = (onlydiffRecords ? diffrows : rows).filter((row) => {
    if (!searchText) return true;

    const search = searchText.toLowerCase();
    return row.data.includes(search) || row.key.toLowerCase().includes(search);
  })
    .sort((a, b) => {
      const aNotFound =
        a.source?.props?.children?.[1]?.props?.children === "Record Not found" ||
        a.destination?.props?.children?.[1]?.props?.children === "Record Not found";
      const bNotFound =
        b.source?.props?.children?.[1]?.props?.children === "Record Not found" ||
        b.destination?.props?.children?.[1]?.props?.children === "Record Not found";

      if (aNotFound && !bNotFound) return 1;
      if (!aNotFound && bNotFound) return -1;
      return 0;
    });

  const handleDownload = () => {
    const flatRows = flattenDiffData(data);
    const csvData = toCSV(flatRows);
    downloadCSV(csvData);
  };

  const handleprev = () => {
    setSelectedDiv("div3");
  };

  return (
    <div style={{ height: "90vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      {/* 🔹 Top bar with search on left, buttons on right */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <Input.Search
          placeholder="Search records..."
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 250 }}
        />
        <div style={{ textAlign: "right" }}>
          <Button style={{ marginRight: 5 }} onClick={handleprev}>
            Back to Relationships
          </Button>
          <Button style={{ marginRight: 5 }} onClick={toggleExpand}>
            {globalExpand ? "Collapse All" : "Expand All"}
          </Button>
          <Button style={{ marginRight: 5 }} onClick={toggleDiffRecords}>
            {onlydiffRecords ? "All Records" : "Show Only Different"}
          </Button>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownload}>
            Download CSV
          </Button>
        </div>
      </div>

      <Table
        loading={loading}
        columns={[
          { title: `${sourcename} (${sourceRecords})`, dataIndex: "source", key: "source", sorter: (a, b) => a.key.localeCompare(b.key) },
          { title: `${destinationname} (${destinationRecords})`, dataIndex: "destination", key: "destination", sorter: (a, b) => a.key.localeCompare(b.key) },
        ]}
        dataSource={filteredRows}
        pagination={false}
        bordered
        scroll={{ y: "calc(90vh - 100px)" }}
        style={{ flex: 1 }}
      />
    </div>
  );
}

export default MyTableComponent;